import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LinkSimple, X as XIcon, Plus, MagnifyingGlass } from '@phosphor-icons/react';
import { WorkTypeIcon } from './KanbanBoard';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

// Five Jira-style relations. Symmetric ones (relates_to) and pairs (blocks/is_blocked_by).
const LINK_TYPES = [
  { value: 'blocks', label: 'blocks', heading: 'Blocks', tone: 'red' },
  { value: 'is_blocked_by', label: 'is blocked by', heading: 'Is blocked by', tone: 'amber' },
  { value: 'relates_to', label: 'relates to', heading: 'Relates to', tone: 'blue' },
  { value: 'duplicates', label: 'duplicates', heading: 'Duplicates', tone: 'violet' },
  { value: 'is_duplicated_by', label: 'is duplicated by', heading: 'Is duplicated by', tone: 'violet' },
];
const TONE_BADGE = {
  red: 'bg-red-50 text-red-700 border-red-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
};
const STATUS_LABELS = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done' };
const STATUS_DOT = {
  todo: 'bg-zinc-400', in_progress: 'bg-blue-500', review: 'bg-amber-500', done: 'bg-emerald-500',
};

// LinkedIssuesSection — list + create + delete cross-task relationships.
export default function LinkedIssuesSection({ task, projectId, allTasks = [], canEdit }) {
  const { getOrgHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [linkType, setLinkType] = useState('blocks');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const headers = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const fetchLinks = useCallback(async () => {
    if (!task?.task_id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tasks/${task.task_id}/links`, headers());
      setLinks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [task?.task_id, API, headers]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  // Group links by type → render Jira-style sections.
  const grouped = useMemo(() => {
    const map = {};
    for (const lt of LINK_TYPES) map[lt.value] = [];
    for (const l of links) {
      if (!l.target || l.target.is_deleted) continue;
      if (!map[l.link_type]) map[l.link_type] = [];
      map[l.link_type].push(l);
    }
    return map;
  }, [links]);

  // Candidates for "add link": tasks in the same project, excluding self,
  // excluding ones already linked with the chosen type.
  const candidates = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.trim().toLowerCase();
    const linkedTargetsForType = new Set(
      links.filter(l => l.link_type === linkType).map(l => l.target_task_id)
    );
    return (allTasks || [])
      .filter(t =>
        t.task_id !== task?.task_id &&
        !t.is_deleted &&
        !linkedTargetsForType.has(t.task_id) &&
        ((t.title || '').toLowerCase().includes(q) || (t.task_key || '').toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [search, allTasks, task?.task_id, links, linkType]);

  const createLink = async (target) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/tasks/${task.task_id}/links`, {
        target_task_id: target.task_id, link_type: linkType,
      }, headers());
      toast.success(`Linked ${target.task_key} as "${LINK_TYPES.find(t => t.value === linkType).label}"`);
      setSearch('');
      setAdding(false);
      fetchLinks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create link');
    } finally {
      setSubmitting(false);
    }
  };

  const removeLink = async (linkId) => {
    try {
      await axios.delete(`${API}/links/${linkId}`, headers());
      setLinks(prev => prev.filter(l => l.link_id !== linkId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove link');
    }
  };

  const totalLinks = links.filter(l => l.target && !l.target.is_deleted).length;

  return (
    <div data-testid="linked-issues-section">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <LinkSimple size={14} className="text-zinc-500" />
          <span className="text-[12px] font-bold text-zinc-700 uppercase tracking-wide">
            Links {totalLinks > 0 && <span className="text-zinc-400">({totalLinks})</span>}
          </span>
        </div>
        {canEdit && !adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} className="h-7 text-[11px]" data-testid="link-add-button">
            <Plus size={12} className="mr-1" /> Link issue
          </Button>
        )}
      </div>

      {/* Add form */}
      {adding && canEdit && (
        <div className="mb-3 p-3 rounded-lg border border-zinc-200 bg-zinc-50/50" data-testid="link-add-form">
          <div className="flex gap-2 mb-2">
            <Select value={linkType} onValueChange={setLinkType}>
              <SelectTrigger className="h-8 w-44 text-[12px]" data-testid="link-type-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LINK_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value} className="text-[12px]" data-testid={`link-type-option-${t.value}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by key or title..."
                className="h-8 pl-7 text-[12px]"
                autoFocus
                data-testid="link-search-input"
              />
            </div>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setSearch(''); }} className="h-8 text-[11px]" data-testid="link-cancel">
              Cancel
            </Button>
          </div>
          {search.trim() && (
            <div className="border border-zinc-200 rounded-md bg-white max-h-56 overflow-auto">
              {candidates.length === 0 ? (
                <div className="p-2 text-[12px] text-zinc-500 text-center">No matching tasks</div>
              ) : (
                candidates.map(t => (
                  <button
                    key={t.task_id}
                    onClick={() => createLink(t)}
                    disabled={submitting}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-blue-50 border-b border-zinc-100 last:border-0 transition-colors"
                    data-testid={`link-candidate-${t.task_id}`}
                  >
                    <WorkTypeIcon type={t.work_type || 'task'} size={13} />
                    <span className="text-[11px] font-mono text-zinc-500">{t.task_key}</span>
                    <span className="text-[12px] text-zinc-800 truncate flex-1">{t.title}</span>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_DOT[t.status] || 'bg-zinc-400'}`} />
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Linked-issues list grouped by type */}
      {totalLinks === 0 && !adding ? (
        <div className="text-[12px] text-zinc-400 italic py-2">
          {canEdit ? 'No linked issues yet. Use “Link issue” to add dependencies.' : 'No linked issues.'}
        </div>
      ) : (
        <div className="space-y-3">
          {LINK_TYPES.map(lt => {
            const items = grouped[lt.value] || [];
            if (!items.length) return null;
            return (
              <div key={lt.value} data-testid={`link-group-${lt.value}`}>
                <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold mb-1">{lt.heading}</div>
                <div className="space-y-1">
                  {items.map(l => {
                    const t = l.target;
                    return (
                      <div
                        key={l.link_id}
                        className="group flex items-center gap-2 px-2 py-1.5 rounded-md border border-zinc-200 bg-white hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                        data-testid={`linked-issue-${l.link_id}`}
                      >
                        <WorkTypeIcon type={t.work_type || 'task'} size={13} />
                        <button
                          onClick={() => navigate(`/tasks/${t.task_id}`)}
                          className="text-[11px] font-mono text-blue-600 hover:underline font-semibold"
                          data-testid={`linked-issue-key-${l.link_id}`}
                        >
                          {t.task_key}
                        </button>
                        <span className="text-[12px] text-zinc-800 truncate flex-1">{t.title}</span>
                        <Badge className={`text-[9px] border ${TONE_BADGE[lt.tone]} font-medium uppercase tracking-wide`}>
                          {STATUS_LABELS[t.status] || t.status}
                        </Badge>
                        {canEdit && (
                          <button
                            onClick={() => removeLink(l.link_id)}
                            className="w-5 h-5 flex items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove link"
                            data-testid={`linked-issue-remove-${l.link_id}`}
                          >
                            <XIcon size={11} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
